import React, { memo, useCallback } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import FullWidgetLink from '../FullWidgetLink';
import DateTimeRange from '../DateTimeRange/DateTimeRange';
import BidNowButton from '../BidNowButton';
import AuctionImage from '../AuctionImage';
import { truncate } from '../../utils/strings';
import {
    getAuctionUrl,
    getAuctioneerUrl
} from '../../utils/urls';
import { AUCTION_PROP_TYPE } from '../../utils/props';
import {
    getFeature,
    FEATURE__HIGHLIGHT_PREMIUM,
    FEATURE__SHOWCASE
} from '../../utils/features';
import {
    getAuctioneerDesignations,
    getAuctionCityState,
} from '../../utils/auctions';
import { prefetchAuction } from '../../gateways/auctionlook_api';
import CountryBadge from '../CountryBadge';

import './AuctionPreview.css';

// Preload an image in the background
var preloadImage = function(url) {
    if (url) {
        var img = new Image();
        img.src = url;
    }
};

const AuctionPreview = memo(({ auction }) => {
    const premium = getFeature(FEATURE__HIGHLIGHT_PREMIUM) && auction.premium;
    const showcase = getFeature(FEATURE__SHOWCASE);
    const auctionUrl = getAuctionUrl(auction.id);
    const auctioneerUrl = getAuctioneerUrl(auction.userId);
    const cityState = getAuctionCityState(auction);
    const title = truncate(auction.title, showcase ? 30 : 60);
    const rawDescription = truncate(auction.description, showcase ? 150 : 200);
    const description = rawDescription && rawDescription.length > 30 ? rawDescription : null;
    const designations = getAuctioneerDesignations(auction);

    // Prefetch auction data and main image on hover
    const handleMouseEnter = useCallback(function() {
        // Prefetch the full auction data
        prefetchAuction(auction.id);

        // Also preload the main photo at full size if available
        var photos = auction.photos;
        if (photos && photos.length > 0 && photos[0] && photos[0].url) {
            preloadImage(photos[0].url);
        }
    }, [auction.id, auction.photos]);

    return (
        <article
            className={`al-auction-card ${premium ? 'al-auction-card--premium' : ''}`}
            onMouseEnter={handleMouseEnter}
        >
            {/* Premium Badge */}
            {premium && (
                <div className="al-card-badge">Premium</div>
            )}

            {/* Hero Image */}
            <AuctionImage
                photo={auction.photos && auction.photos[0]}
                showcase={showcase}
                link={auctionUrl}
            />

            {/* Card Body */}
            <div className="al-card-body">
                {/* Title */}
                <h2 className="al-card-title">
                    <FullWidgetLink to={auctionUrl} title={`View ${auction.title}`}>
                        {title}
                    </FullWidgetLink>
                </h2>

                {/* Location */}
                <div className="al-card-location">
                    <MapPin size={14} />
                    <span>{cityState}</span>
                    <CountryBadge countryId={auction.countryId} />
                </div>

                {/* Description */}
                {description && (
                    <p className="al-card-description">
                        <FullWidgetLink to={auctionUrl}>
                            {description}
                        </FullWidgetLink>
                    </p>
                )}

                {/* Auctioneer */}
                <div className="al-card-auctioneer">
                    <FullWidgetLink to={auctioneerUrl} title={`About ${auction.auctioneerName}`}>
                        {auction.auctioneerName}
                        {designations && <span className="al-designations">{designations}</span>}
                    </FullWidgetLink>
                </div>
            </div>

            {/* Card Footer */}
            <footer className="al-card-footer">
                <div className="al-card-date">
                    <Calendar size={14} />
                    <DateTimeRange
                        startDate={auction.date}
                        startTime={auction.beginTime}
                        endDate={auction.endDate}
                        endTime={auction.endTime}
                        countryId={auction.countryId}
                        compact={true}
                    />
                </div>
                <div className="al-card-actions">
                    {auction.biddingUrl ? (
                        <BidNowButton
                            title={auction.title}
                            biddingUrl={auction.biddingUrl}
                            premium={premium}
                        />
                    ) : (
                        <FullWidgetLink
                            to={auctionUrl}
                            className="al-button al-button--primary"
                            title={'View ' + auction.title}
                        >
                            View Details
                        </FullWidgetLink>
                    )}
                </div>
            </footer>
        </article>
    );
});

AuctionPreview.propTypes = {
    auction: AUCTION_PROP_TYPE.isRequired
};

// Custom comparison - only re-render if auction ID changes
AuctionPreview.displayName = 'AuctionPreview';

export default AuctionPreview;
